import { Stack, TextInput, Group, Text, ActionIcon, Divider, Box, Select, SimpleGrid, Grid } from "@mantine/core";
import { Button, Alert } from "@xplatform/design-system";
import { IconPlus, IconX } from "@tabler/icons-react";
import { ModelTierEditor } from "./ModelTierEditor";
import type { FormState, PricingTier } from "../../utils/modelModalHelper";
import { emptyTier } from "../../utils/modelModalHelper";

interface ModelFormProps {
    form: FormState;
    setForm: React.Dispatch<React.SetStateAction<FormState>>;
    editingId: string | null;
    error: string | null;
    loading: boolean;
    onCancel: () => void;
    onSubmit: () => void;
}

export const ModelForm = ({
    form,
    setForm,
    editingId,
    error,
    loading,
    onCancel,
    onSubmit,
}: ModelFormProps) => {
    const addTier = (type: "input" | "output") =>
        setForm((f) => {
            const key = type === "input" ? "inputTiers" : "outputTiers";
            return { ...f, [key]: [...f[key], emptyTier()] };
        });

    const removeTier = (type: "input" | "output", i: number) =>
        setForm((f) => {
            const key = type === "input" ? "inputTiers" : "outputTiers";
            const next = f[key].filter((_, idx) => idx !== i);
            return { ...f, [key]: next.length ? next : [emptyTier()] };
        });

    const updateTier = (
        type: "input" | "output",
        i: number,
        field: keyof PricingTier,
        val: number | "",
    ) =>
        setForm((f) => {
            const key = type === "input" ? "inputTiers" : "outputTiers";
            return {
                ...f,
                [key]: f[key].map((t, idx) => (idx === i ? { ...t, [field]: val } : t)),
            };
        });

    const addMetadataRow = () =>
        setForm((f) => ({ ...f, metadata: [...f.metadata, { key: "", value: "" }] }));

    const removeMetadataRow = (i: number) =>
        setForm((f) => ({ ...f, metadata: f.metadata.filter((_, idx) => idx !== i) }));

    const updateMetadataRow = (i: number, field: "key" | "value", val: string) =>
        setForm((f) => ({
            ...f,
            metadata: f.metadata.map((e, idx) => (idx === i ? { ...e, [field]: val } : e)),
        }));

    return (
        <Stack gap="md">
            <Divider label={editingId ? "Edit Model" : "New Model"} labelPosition="left" />
            {error && (
                <Alert color="red" icon={<IconX size={14} />}>
                    {error}
                </Alert>
            )}

            <Divider label="Basic Info" labelPosition="left" />
            <Grid>
                <Grid.Col span={6}>
                    <TextInput
                        label="Model Name"
                        placeholder="e.g. gpt-4o"
                        required
                        disabled={!!editingId}
                        value={form.modelName}
                        onChange={(e) => {
                            const v = e.currentTarget.value;
                            setForm((f) => ({ ...f, modelName: v }));
                        }}
                    />
                </Grid.Col>
                <Grid.Col span={6}>
                    <TextInput
                        label="Display Name"
                        placeholder="Optional friendly name"
                        value={form.displayName}
                        onChange={(e) => {
                            const v = e.currentTarget.value;
                            setForm((f) => ({ ...f, displayName: v }));
                        }}
                    />
                </Grid.Col>
                <Grid.Col span={6}>
                    <Select
                        label="Status"
                        data={[
                            { value: "active", label: "Active" },
                            { value: "disabled", label: "Disabled" },
                        ]}
                        value={form.status}
                        onChange={(v) => setForm((f) => ({ ...f, status: v ?? "active" }))}
                    />
                </Grid.Col>
                <Grid.Col span={6}>
                    <Select
                        label="Currency"
                        data={["USD", "EUR", "GBP"]}
                        value={form.currency}
                        onChange={(v) => setForm((f) => ({ ...f, currency: v ?? "USD" }))}
                    />
                </Grid.Col>
            </Grid>

            <Divider label="Pricing (per 1M tokens)" labelPosition="left" />
            <SimpleGrid cols={2} spacing="xl">
                <ModelTierEditor
                    type="input"
                    tiers={form.inputTiers}
                    onAdd={() => addTier("input")}
                    onRemove={(i) => removeTier("input", i)}
                    onUpdate={(i, field, val) => updateTier("input", i, field, val)}
                />
                <ModelTierEditor
                    type="output"
                    tiers={form.outputTiers}
                    onAdd={() => addTier("output")}
                    onRemove={(i) => removeTier("output", i)}
                    onUpdate={(i, field, val) => updateTier("output", i, field, val)}
                />
            </SimpleGrid>

            <Divider label="Metadata" labelPosition="left" />
            <Box>
                <Group justify="flex-end" mb={4}>
                    <Button
                        size="xs"
                        variant="subtle"
                        leftSection={<IconPlus size={12} />}
                        onClick={addMetadataRow}
                    >
                        Add field
                    </Button>
                </Group>
                <Stack gap={6}>
                    {form.metadata.map((entry, i) => (
                        <Group key={i} gap={6} align="flex-end">
                            <TextInput
                                placeholder="key"
                                size="xs"
                                style={{ flex: 1 }}
                                value={entry.key}
                                onChange={(e) => updateMetadataRow(i, "key", e.currentTarget.value)}
                            />
                            <TextInput
                                placeholder="value"
                                size="xs"
                                style={{ flex: 2 }}
                                value={entry.value}
                                onChange={(e) =>
                                    updateMetadataRow(i, "value", e.currentTarget.value)
                                }
                            />
                            <ActionIcon
                                size="sm"
                                color="red"
                                variant="subtle"
                                onClick={() => removeMetadataRow(i)}
                            >
                                <IconX size={12} />
                            </ActionIcon>
                        </Group>
                    ))}
                    {form.metadata.length === 0 && (
                        <Text size="xs" c="dimmed">
                            No metadata fields
                        </Text>
                    )}
                </Stack>
            </Box>

            <Group justify="flex-end">
                <Button variant="default" onClick={onCancel} disabled={loading}>
                    Cancel
                </Button>
                <Button onClick={onSubmit} loading={loading}>
                    {editingId ? "Update" : "Add"}
                </Button>
            </Group>
        </Stack>
    );
};
